/**
 * Dashboard Page Component - Redesigned
 * Focuses on WHOT and Chess as primary games with sleek modern design
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { platformAPI, userAPI } from '../services/api';
import api from '../services/api';
import { chessAPI, checkersAPI, draughtsAPI, flappyAPI, reactionAPI } from '../services/api';
import { connectSocket, getSocket } from '../services/socket';
import { connectWhotOnlineSocket, whotOnlineSocket } from '../services/whotOnlineSocket';
import {
  getGlobalSoundEnabled,
  setGlobalSoundEnabled,
  subscribeToGlobalSoundPreference,
} from '../utils/soundPreferences';
import Friends from '../components/Friends';
import Chat from '../components/Chat';

import Footer from '../components/Footer';
import '../styles/Dashboard2.css';

const Dashboard = () => {
  const QUICK_MATCH_POLL_MS = 2000;
  const QUICK_MATCH_TIMEOUT_MS = 60 * 1000;
  const DEFAULT_MINIMUM_WAGER = 50;
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [wager, setWager] = useState(DEFAULT_MINIMUM_WAGER);
  const [minimumWager, setMinimumWager] = useState(DEFAULT_MINIMUM_WAGER);
  const [onlineMembers, setOnlineMembers] = useState(2847);
  const [referralBonusPercent, setReferralBonusPercent] = useState(10);
  const [inQueue, setInQueue] = useState(false);
  const [queueStatus, setQueueStatus] = useState('');
  const [error, setError] = useState('');
  const [adminMessages, setAdminMessages] = useState([]);
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [activeMultiplayerChallenge, setActiveMultiplayerChallenge] = useState(null);
  const [activeWhotRoom, setActiveWhotRoom] = useState(null);
  const [activeGameCards, setActiveGameCards] = useState([]);
  const [pendingDirectChallenges, setPendingDirectChallenges] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [selectedFriendForChat, setSelectedFriendForChat] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [globalSoundEnabled, setGlobalSoundEnabledState] = useState(() => getGlobalSoundEnabled());
  const [friendsOpenSignal, setFriendsOpenSignal] = useState(0);
  const [quickQueueing, setQuickQueueing] = useState(false);
  const [quickStatus, setQuickStatus] = useState('');
  const [referralStatus, setReferralStatus] = useState('');
  const [activeFeaturedGame, setActiveFeaturedGame] = useState('home');
  const [resolvedFeaturedImages, setResolvedFeaturedImages] = useState({});
  const [isMobileCarousel, setIsMobileCarousel] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 760px)').matches;
  });
  const referralStatusTimeoutRef = useRef(null);
  const quickLaunchSectionRef = useRef(null);
  const supportSectionRef = useRef(null);
  const quickMatchSessionRef = useRef(0);
  const quickMatchChallengeIdRef = useRef(null);

  const wins = Number(user?.wins || 0);
  const losses = Number(user?.losses || 0);
  const winRate = wins + losses > 0 ? ((wins / (wins + losses)) * 100).toFixed(2) : '0.00';
  const isGuest = !user;
  const anonymousGamePath = '/flappy/practice';
  const anonymousWhotPath = '/whot/practice';
  const fallbackReferralCode = user?.id ? `WG${user.id}` : '';
  const referralCode = user?.referral_code || fallbackReferralCode;
  const appOrigin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
  const referralLink = referralCode && appOrigin
    ? `${appOrigin}/register?ref=${encodeURIComponent(referralCode)}`
    : '';
  const supportContacts = useMemo(() => ([
    {
      label: 'Email',
       value: 'Click to send us email',
      hint: 'Primary help desk for payments, account issues, and moderation questions.',
      href: 'mailto:wikigamesupport@gmail.com',
    },
    {
      label: 'WhatsApp',
      value: 'Chat on Whatsapp',
      hint: 'Click now for Fast support for deposit, withdrawal, and wallet problems.',
      href: 'https://wa.me/2348026233496',
    },
    {
      label: 'Telegram',
      value: '@wikigamecommunity',
      hint: 'Live chat channel for general player support.',
      href: 'https://t.me/wikigamecommunity',
    },
  ]), []);

  const defaultWhotRules = useMemo(() => ({
    whotEnabled: true,
    generalMarket14: true,
    pick2: true,
    holdOn1: true,
    pick3On5: true,
    suspension8: true,
  }), []);
  const defaultWhotTurnTime = 10;

  // Featured carousel slides
  const featuredGames = useMemo(() => ([
    {
      key: 'home',
      title: 'Home',
      subtitle: '',
      description: '',
      cta: 'Explore more',
      to: '/dashboard',
      backgroundCandidates: ['/dashboard-carousel/home1.png'],
      backgroundCandidatesMobile: ['/dashboard-carousel/home.png'],
    },
    {
      key: 'whot',
      title: 'WHOT ',
      subtitle: 'The Ultimate Card Battle',
      description: '.',
      cta: 'Play WHOT',
      to: '/whot-online',
      backgroundCandidates: ['/dashboard-carousel/whot2.png', '/dashboard-carousel/whot1.png', '/dashboard-carousel/whot.png'],
      backgroundCandidatesMobile: ['/dashboard-carousel/whot1s.png', '/dashboard-carousel/whot1.png', '/dashboard-carousel/whot.png'],
    },
    {
      key: 'chess',
      title: 'Chess ',
      subtitle: 'Strategic Mastery',
      description: '.',
      cta: 'Play Chess',
      to: '/chess',
      backgroundCandidates: ['/dashboard-carousel/chess2.png', '/dashboard-carousel/chess1.png', '/dashboard-carousel/chess.png'],
      backgroundCandidatesMobile: ['/dashboard-carousel/chess1s.png', '/dashboard-carousel/chess1.png', '/dashboard-carousel/chess.png'],
    },
    {
      key: 'dice',
      title: 'Dice ',
      subtitle: 'Risk, Read, and Roll',
      description: '.',
      cta: 'Play Dice',
      to: '/challenges',
      backgroundCandidates: ['/dashboard-carousel/dice2.png', '/dashboard-carousel/dice1.png'],
      backgroundCandidatesMobile: ['/dashboard-carousel/dice1s.png', '/dashboard-carousel/dice1.png'],
    },
    {
      key: 'draughts',
      title: 'Draughts ',
      subtitle: 'Draught',
      description: '.',
      cta: 'Play Draughts',
      to: '/draughts/lobby',
      backgroundCandidates: ['/dashboard-carousel/draft.jpg'],
      backgroundCandidatesMobile: ['/dashboard-carousel/draft2.jpg'],
    },
  ]), [user?.chess_rating]);

  const gameModes = useMemo(() => ([
    {
      key: 'whot',
      title: 'WHOT ',
      icon: '🃏',
      description: 'Real-time card battles',
      longDescription: 'Join live WHOT rooms, challenge friends, and climb the ranks',
      to: '/whot-online',
      gradient: 'from-purple-500 to-pink-500',
      badge: 'HOT',
      isMain: true
    },
    {
      key: 'chess',
      title: 'Chess',
      icon: '♟️',
      description: 'Strategic showdowns',
      longDescription: 'Rapid and classic time controls with ELO ranking',
      to: '/chess',
      gradient: 'from-red-500 to-orange-500',
       badge: 'HOT',
      isMain: true
    },
    {
      key: 'checkers',
      title: 'Checkers',
      icon: '⚪',
      description: 'Classic king me duels',
      longDescription: 'Play vs bot, friendly, or real wager challenge with turn timer',
      to: '/checkers/lobby',
      gradient: 'from-amber-500 to-orange-600',
      badge: 'NEW',
      isMain: true
    },
    {
      key: 'draughts',
      title: 'Draughts',
      icon: '🏁',
      description: '10×10 board',
      longDescription: 'Flying kings, compulsory captures, free capture paths — real Nigerian Draft',
      to: '/draughts/lobby',
      gradient: 'from-lime-500 to-green-600',
      badge: 'NEW',
      isMain: true
    },
    {
      key: 'dice',
      title: 'Dice Challenges',
      icon: '🎲',
      description: 'Create & accept challenges',
      to: '/challenges',
      gradient: 'from-green-500 to-teal-500',
       badge: 'HOT',
      /*badge: pendingDirectChallenges.length > 0 ? pendingDirectChallenges.length : null*/
    },
    {
      key: 'lucky',
      title: 'Lucky Number',
      icon: '🍀',
      description: 'Multiplayer lucky picks',
      to: '/multiplayer',
      gradient: 'from-emerald-500 to-lime-500',
      badge: 'HOT'
    },
    {
      key: 'flappy',
      title: 'Flappy Bird',
      icon: '🐦',
      description: 'Timed turn-based duels',
      to: '/flappy',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      key: 'reaction',
      title: 'Reaction Blitz',
      icon: '⚡',
      description: 'Test your reflexes',
      to: '/reaction',
      gradient: 'from-yellow-500 to-red-500'
    }
  ]), [pendingDirectChallenges.length]);

  const dailyChallenges = useMemo(() => ([
    {
      game: 'Referral Bonus',
      challenge: 'Share your referral link — earn 10% of every deposit made by anyone who signs up through it.',
      reward: '10% per deposit',
      icon: '🔗',
      isReferral: true
    },
    {
      game: 'Chess',
      challenge: 'Checkmate in under 10 moves',
      reward: 'Rating +50',
      icon: '♟️'
    }
  ]), []);

  const activeFeatured = featuredGames.find(g => g.key === activeFeaturedGame) || featuredGames[0];
  const activeFeaturedCandidates = isMobileCarousel
    ? (activeFeatured.backgroundCandidatesMobile || activeFeatured.backgroundCandidates)
    : activeFeatured.backgroundCandidates;
  const activeFeaturedIndex = featuredGames.findIndex((g) => g.key === activeFeatured.key);
  const hasActiveResumeCards = Boolean(
    activeChallenge
    || activeMultiplayerChallenge
    || activeWhotRoom
    || activeGameCards.length > 0
  );

  const cycleFeatured = (direction) => {
    const nextIndex = (activeFeaturedIndex + direction + featuredGames.length) % featuredGames.length;
    setActiveFeaturedGame(featuredGames[nextIndex].key);
  };

  function openMainGame(to) {
    if (isGuest) {
      setQuickStatus('Login to play main games. You can try Anonymous Flappy first.');
      navigate('/login', { state: { backgroundLocation: location } });
      return;
    }
    navigate(to);
  }

  function openAnonymousGame() {
    navigate(anonymousGamePath);
  }

  function openAnonymousWhot() {
    navigate(anonymousWhotPath);
  }

  function scrollToQuickLaunch() {
    const target = quickLaunchSectionRef.current;
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function scrollToSupport() {
    const target = supportSectionRef.current;
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function dismissAdminMessage(messageId) {
    try {
      await userAPI.dismissAdminMessage(messageId);
      setAdminMessages((prev) => prev.filter((message) => String(message.id) !== String(messageId)));
    } catch (err) {
      console.error('Failed to dismiss admin message:', err);
    }
  }

  useEffect(() => {
    if (user) {
      refreshUser?.();
    }
  }, [location.key]);

  useEffect(() => {
    if (!user || typeof window === 'undefined') return;

    try {
      const raw = window.localStorage.getItem('wikigame:pending-welcome');
      if (!raw) return;

      const payload = JSON.parse(raw);
      if (payload?.type !== 'email_verified') return;

      const username = payload?.username || user?.username || 'Player';
      toast.success(`Welcome ${username}, your email is now verified.`, {
        id: 'email-verified-welcome',
        duration: 6000,
      });
      window.localStorage.removeItem('wikigame:pending-welcome');
    } catch (_error) {
      window.localStorage.removeItem('wikigame:pending-welcome');
    }
  }, [user?.id, user?.username]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('friends') === '1') {
      setFriendsOpenSignal(Date.now());
    }
    if (params.get('profile') === '1') {
      setShowProfileMenu(true);
    }
    if (params.get('messages') === '1') {
      setShowChat(true);
      setSelectedFriendForChat(null);
    }
  }, [location.search]);

  useEffect(() => {
    if (!user) {
      setAdminMessages([]);
      return undefined;
    }

    let cancelled = false;

    const loadAdminMessages = async () => {
      try {
        const response = await userAPI.getAdminMessages();
        if (cancelled) return;
        setAdminMessages(Array.isArray(response?.data?.messages) ? response.data.messages : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load admin messages:', err);
      }
    };

    loadAdminMessages();
    const intervalId = window.setInterval(loadAdminMessages, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      api.get('/challenges/active')
        .then((res) => {
          if (res.data) {
            setActiveChallenge(res.data);
          }
        })
        .catch((err) => console.error('Failed to check active challenge:', err));
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    api.get('/multiplayer/active', { params: { game: 'dice' } })
      .then((res) => {
        setActiveMultiplayerChallenge(res.data || null);
      })
      .catch((err) => console.error('Failed to check active multiplayer challenge:', err));
  }, [user, location.key]);

  useEffect(() => {
    if (!user) return;

    const resumeConfigs = [
      {
        key: 'chess',
        label: 'Chess Match',
        subtitle: 'Your chess game is ongoing',
        icon: '♟️',
        load: () => chessAPI.listOngoingMatches(),
        to: (id) => `/chess/duel/${id}`,
      },
      {
        key: 'checkers',
        label: 'Checkers Match',
        subtitle: 'Your checkers game is ongoing',
        icon: '⚪',
        load: () => checkersAPI.listOngoingMatches(),
        to: (id) => `/checkers/duel/${id}`,
      },
      {
        key: 'draughts',
        label: 'Draughts Match',
        subtitle: 'Your draughts game is ongoing',
        icon: '🏁',
        load: () => draughtsAPI.listOngoingMatches(),
        to: (id) => `/draughts/duel/${id}`,
      },
      {
        key: 'flappy',
        label: 'Flappy Duel',
        subtitle: 'Your flappy duel is ongoing',
        icon: '🐦',
        load: () => flappyAPI.listOngoingMatches(),
        to: (id) => `/flappy/duel/${id}`,
      },
      {
        key: 'reaction',
        label: 'Reaction Match',
        subtitle: 'Your reaction match is ongoing',
        icon: '⚡',
        load: () => reactionAPI.listOngoingMatches(),
        to: (id) => `/reaction/duel/${id}`,
      },
    ];

    const loadActiveGameCards = async () => {
      const results = await Promise.allSettled(resumeConfigs.map((cfg) => cfg.load()));
      const nextCards = results.flatMap((result, index) => {
        if (result.status !== 'fulfilled') return [];
        const rows = Array.isArray(result.value?.data) ? result.value.data : [];
        const match = rows[0];
        if (!match?.id) return [];
        const cfg = resumeConfigs[index];
        return [{
          key: cfg.key,
          label: cfg.label,
          subtitle: cfg.subtitle,
          icon: cfg.icon,
          to: cfg.to(match.id),
          id: match.id,
        }];
      });
      setActiveGameCards(nextCards);
    };

    loadActiveGameCards().catch((err) => {
      console.error('Failed to load active game cards:', err);
      setActiveGameCards([]);
    });
  }, [user, location.key]);

  useEffect(() => {
    if (user) {
      const fetchPendingChallenges = async () => {
        try {
          const res = await api.get('/challenges/pending/direct');
          setPendingDirectChallenges(res.data || []);
        } catch (err) {
          console.error('Failed to fetch pending challenges:', err);
        }
      };

      fetchPendingChallenges();
      const interval = setInterval(fetchPendingChallenges, 2000);

      const socket = getSocket();
      const onDirect = (challenge) => {
        setPendingDirectChallenges(prev => {
          if (prev.some(c => c.id === challenge.id)) return prev;
          return [challenge, ...prev];
        });
      };
      if (socket) socket.on('challenge:direct:new', onDirect);

      return () => {
        clearInterval(interval);
        if (socket) socket.off('challenge:direct:new', onDirect);
      };
    }
  }, [user]);

  useEffect(() => {
    if (user && token) {
      connectSocket(user.id, token);
    }

    return undefined;
  }, [user, token]);

  useEffect(() => {
    if (!token) return;
    connectWhotOnlineSocket(token);

    const syncActiveWhotRoom = (rooms = []) => {
      const myUserId = Number(user?.id || 0);
      if (!myUserId) {
        setActiveWhotRoom(null);
        return;
      }

      const roomList = Array.isArray(rooms) ? rooms : [];
      const myRooms = roomList.filter((room) => (
        (room?.playerIds || []).some((id) => Number(id) === myUserId)
        && ['waiting', 'in_progress'].includes(String(room?.gameStatus || '').toLowerCase())
      ));

      if (!myRooms.length) {
        setActiveWhotRoom(null);
        return;
      }

      const preferredRoom = myRooms.find((room) => String(room?.gameStatus || '').toLowerCase() === 'in_progress')
        || myRooms[0];
      setActiveWhotRoom(preferredRoom);
    };

    whotOnlineSocket.onQueueMatched((payload) => {
      if (!payload?.roomId) return;
      setQuickQueueing(false);
      navigate(`/whot-online/room/${payload.roomId}`);
    });
    whotOnlineSocket.onQueueError((payload) => {
      const message = payload?.message || 'Match failed';
      setQuickQueueing(false);
      setQuickStatus(message);
    });

    whotOnlineSocket.onRoomsUpdate((payload) => {
      syncActiveWhotRoom(payload?.rooms || []);
    });

    whotOnlineSocket.listRooms((resp) => {
      if (!resp?.ok) return;
      syncActiveWhotRoom(resp?.rooms || []);
    });

    return () => {
      whotOnlineSocket.removeListeners();
    };
  }, [token, navigate, user?.id]);

  useEffect(() => {
    let timeoutId;
    let cancelled = false;

    const loadPlatformSettings = async () => {
      try {
        const res = await platformAPI.getSettings();
        const nextMin = Number(res?.data?.minimumWager);
        const nextOnlineMembers = Number(res?.data?.onlineMembers);
        const nextReferralBonus = Number(res?.data?.referralBonusPercent);
        const nextOnlineMembersMode = String(res?.data?.onlineMembersMode || '').toLowerCase();
        const nextRandomIntervalSeconds = Number(res?.data?.onlineMembersRandomIntervalSeconds);
        if (Number.isFinite(nextMin) && nextMin >= 0) {
          setMinimumWager(nextMin);
          setWager((prev) => Math.max(nextMin, Number(prev) || 0));
        }
        if (Number.isFinite(nextOnlineMembers) && nextOnlineMembers >= 0) {
          setOnlineMembers(Math.trunc(nextOnlineMembers));
        }
        if (Number.isFinite(nextReferralBonus) && nextReferralBonus >= 0) {
          setReferralBonusPercent(nextReferralBonus);
        }

        const fallbackIntervalMs = 15000;
        const randomIntervalMs = Number.isFinite(nextRandomIntervalSeconds) && nextRandomIntervalSeconds > 0
          ? Math.max(5000, Math.trunc(nextRandomIntervalSeconds * 1000))
          : fallbackIntervalMs;
        const nextPollMs = nextOnlineMembersMode === 'random' ? randomIntervalMs : fallbackIntervalMs;

        if (!cancelled) {
          timeoutId = window.setTimeout(loadPlatformSettings, nextPollMs);
        }
      } catch (err) {
        console.error('Failed to load platform settings:', err);
        if (!cancelled) {
          timeoutId = window.setTimeout(loadPlatformSettings, 15000);
        }
      }
    };

    loadPlatformSettings();
    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    return subscribeToGlobalSoundPreference(setGlobalSoundEnabledState);
  }, []);

  useEffect(() => {
    const onMouseDown = (e) => {
      const container = document.querySelector('.profile-menu-container');
      if (!container) return;
      if (!container.contains(e.target)) setShowProfileMenu(false);
    };

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const handleJoinQueue = async () => {
    const parsedWager = Math.trunc(Number(wager) || 0);
    if (!Number.isFinite(parsedWager) || parsedWager <= 0) {
      setError('Enter a valid wager amount');
      return;
    }

    let liveMinimumWager = minimumWager;
    try {
      const settingsRes = await platformAPI.getSettings();
      const nextMin = Number(settingsRes?.data?.minimumWager);
      if (Number.isFinite(nextMin) && nextMin >= 0) {
        liveMinimumWager = Math.trunc(nextMin);
        setMinimumWager(liveMinimumWager);
      }
    } catch (settingsError) {
      // Fall back to current in-memory minimum when settings fetch fails.
    }

    if (parsedWager < liveMinimumWager) {
      setError(`Wager must be at least ${liveMinimumWager} wikipenny`);
      setWager(liveMinimumWager);
      return;
    }

    if (Number(user?.balance || 0) < parsedWager) {
      setError('Insufficient balance');
      return;
    }

    try {
      const currentUserId = Number(user?.id || 0);
      const sessionId = quickMatchSessionRef.current + 1;
      quickMatchSessionRef.current = sessionId;

      setError('');
      setInQueue(true);
      setQueueStatus('Searching for dice opponent...');

      const openRes = await api.get('/challenges/open', { params: { game: 'dice' } });
      const openChallenges = Array.isArray(openRes.data) ? openRes.data : [];
      const matchingOpen = openChallenges.find((challenge) => (
        Number(challenge?.creator_id) !== currentUserId && Number(challenge?.wager || 0) === parsedWager
      ));

      if (matchingOpen) {
        await api.post(`/challenges/${matchingOpen.id}/accept`);
        if (quickMatchSessionRef.current !== sessionId) return;
        setInQueue(false);
        setQueueStatus('');
        navigate(`/challenges/${matchingOpen.id}`);
        return;
      }

      const createdRes = await api.post('/challenges', { wager: parsedWager, game: 'dice', numGames: 1 });
      const createdId = Number(createdRes?.data?.id);
      if (!Number.isFinite(createdId) || createdId <= 0) {
        throw new Error('Failed to create quick match challenge');
      }
      quickMatchChallengeIdRef.current = createdId;

      const startedAt = Date.now();
      while (quickMatchSessionRef.current === sessionId && (Date.now() - startedAt) < QUICK_MATCH_TIMEOUT_MS) {
        await new Promise((resolve) => setTimeout(resolve, QUICK_MATCH_POLL_MS));
        if (quickMatchSessionRef.current !== sessionId) return;

        try {
          const challengeRes = await api.get(`/challenges/${createdId}`);
          const challenge = challengeRes?.data;
          const hasOpponent = Number(challenge?.opponent_id || 0) > 0;
          const status = String(challenge?.status || '').toLowerCase();
          if (hasOpponent || status === 'accepted' || status === 'in_progress') {
            if (quickMatchSessionRef.current !== sessionId) return;
            quickMatchChallengeIdRef.current = null;
            setInQueue(false);
            setQueueStatus('');
            navigate(`/challenges/${createdId}`);
            return;
          }
        } catch {
          // Continue polling; transient fetch errors should not stop matchmaking.
        }
      }

      if (quickMatchSessionRef.current === sessionId) {
        try {
          await api.delete(`/challenges/${createdId}`);
        } catch {
          // Ignore cleanup failures.
        }
        quickMatchChallengeIdRef.current = null;
        setInQueue(false);
        setQueueStatus('');
        setError('Server is busy for now. Try again later.');
      }
    } catch (err) {
      quickMatchChallengeIdRef.current = null;
      setInQueue(false);
      setQueueStatus('');
      setError(err.response?.data?.error || 'Failed to join queue');
    }
  };

  const handleLeaveQueue = async () => {
    try {
      quickMatchSessionRef.current += 1;
      const activeChallengeId = quickMatchChallengeIdRef.current;
      quickMatchChallengeIdRef.current = null;
      if (activeChallengeId) {
        try {
          await api.delete(`/challenges/${activeChallengeId}`);
        } catch {
          // Ignore if already accepted/deleted by server.
        }
      }
      setInQueue(false);
      setQueueStatus('');
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to leave queue');
    }
  };

  useEffect(() => () => {
    const activeChallengeId = quickMatchChallengeIdRef.current;
    quickMatchSessionRef.current += 1;
    quickMatchChallengeIdRef.current = null;
    if (activeChallengeId) {
      void api.delete(`/challenges/${activeChallengeId}`).catch(() => {});
    }
  }, []);

  const handleQuickWhot = () => {
    if (quickQueueing) return;
    setQuickStatus('');
    setQuickQueueing(true);
    whotOnlineSocket.queueJoin({
      houseRules: defaultWhotRules,
      turnTimeSeconds: defaultWhotTurnTime,
    }, (resp) => {
      if (!resp?.ok) {
        const message = resp?.error || 'Failed to join queue';
        setQuickQueueing(false);
        setQuickStatus(message);
      }
    });
  };

  const showReferralStatus = (message) => {
    setReferralStatus(message);
    if (referralStatusTimeoutRef.current) {
      clearTimeout(referralStatusTimeoutRef.current);
    }
    referralStatusTimeoutRef.current = setTimeout(() => {
      setReferralStatus('');
    }, 2200);
  };

  const copyReferralValue = async (value, label) => {
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.setAttribute('readonly', 'true');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      showReferralStatus(`${label} copied.`);
    } catch (copyError) {
      console.error('Failed to copy referral data:', copyError);
      showReferralStatus('Copy failed. Please copy manually.');
    }
  };

  const shareReferralOnWhatsApp = () => {
    if (!referralLink) {
      showReferralStatus('Referral link is not ready yet.');
      return;
    }
    const message = `Join me on WikiGame and use my referral code ${referralCode} during signup: ${referralLink}`;
    const shareUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => () => {
    if (referralStatusTimeoutRef.current) {
      clearTimeout(referralStatusTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mediaQuery = window.matchMedia('(max-width: 760px)');
    const onChange = (event) => setIsMobileCarousel(Boolean(event.matches));
    setIsMobileCarousel(Boolean(mediaQuery.matches));

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onChange);
      return () => mediaQuery.removeEventListener('change', onChange);
    }

    mediaQuery.addListener(onChange);
    return () => mediaQuery.removeListener(onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const probeImage = (src) => new Promise((resolve) => {
      if (typeof Image === 'undefined') {
        resolve(null);
        return;
      }
      const img = new Image();
      img.onload = () => resolve(src);
      img.onerror = () => resolve(null);
      img.src = src;
    });

    const resolveFeaturedImages = async () => {
      const entries = await Promise.all(
        featuredGames.map(async (game) => {
          const candidates = isMobileCarousel
            ? (game.backgroundCandidatesMobile || game.backgroundCandidates || [])
            : (game.backgroundCandidates || []);

          for (const candidate of candidates) {
            const loaded = await probeImage(candidate);
            if (loaded) return [game.key, loaded];
          }
          return [game.key, candidates[0] || ''];
        })
      );

      if (!cancelled) {
        setResolvedFeaturedImages(Object.fromEntries(entries));
      }
    };

    resolveFeaturedImages();

    return () => {
      cancelled = true;
    };
  }, [featuredGames, isMobileCarousel]);

  useEffect(() => {
    const rotateTimer = setInterval(() => {
      setActiveFeaturedGame((current) => {
        const currentIndex = featuredGames.findIndex((game) => game.key === current);
        const nextIndex = (currentIndex + 1) % featuredGames.length;
        return featuredGames[nextIndex].key;
      });
    }, 6000);

    return () => clearInterval(rotateTimer);
  }, [featuredGames]);

  return (
    <div className="dashboard-container dashboard-redesign">
      <div className="dashboard-inner">
        {/* Animated Background */}
        <div className="animated-bg">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>

        <main className="dashboard-content">
          {adminMessages.length > 0 && (
            <section style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
              {adminMessages.map((message) => (
                <div
                  key={message.id}
                  style={{
                    position: 'relative',
                    padding: '16px 18px',
                    borderRadius: 18,
                    background: 'linear-gradient(135deg, rgba(250,204,21,0.18), rgba(249,115,22,0.16))',
                    border: '1px solid rgba(251,191,36,0.32)',
                    boxShadow: '0 12px 36px rgba(15,23,42,0.14)',
                  }}
                >
                  <div style={{ paddingRight: message.dismissible !== false ? 42 : 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6, opacity: 0.72 }}>
                      Admin Update
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>{message.title}</div>
                    <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', opacity: 0.92 }}>{message.body}</div>
                  </div>
                  {message.dismissible !== false && (
                    <button
                      type="button"
                      onClick={() => dismissAdminMessage(message.id)}
                      aria-label="Dismiss announcement"
                      style={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        width: 32,
                        height: 32,
                        borderRadius: 999,
                        border: 'none',
                        background: 'rgba(255,255,255,0.72)',
                        cursor: 'pointer',
                        fontSize: 18,
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </section>
          )}

          {/* Hero Section with Featured Games */}
          <section className="hero-section">
            <div className="hero-glass-card">
              <div className="hero-badge">
                <span className="live-badge">LIVE</span>
                <span className="players-count">{onlineMembers.toLocaleString()} players online</span>
              </div>
              
              <div className="featured-game-content">
                <article
                  className={`featured-slide slide-${activeFeatured.key}`}
                  style={{
                    backgroundImage: `url(${resolvedFeaturedImages[activeFeatured.key] || activeFeaturedCandidates?.[0] || ''})`
                  }}
                >
                  {activeFeatured.key === 'home' && (
                    <div className="featured-home-copy">
                      <p className="featured-home-intro">Welcome to</p>
                      <h2 className="featured-home-brand">WikiGames</h2>
                      <p className="featured-home-caption">First ever crypto gaming community</p>
                    </div>
                  )}

                  <div className="featured-cta-wrap">
                    <button
                      className="btn-play-now"
                      onClick={() => {
                        if (activeFeatured.key === 'home') {
                          scrollToQuickLaunch();
                          return;
                        }
                        openMainGame(activeFeatured.to);
                      }}
                    >
                      {activeFeatured.key === 'home' ? 'Explore more' : (activeFeatured.cta || 'Play')}
                    </button>
                  </div>

                  <div className="featured-carousel-controls" aria-label="Featured game controls">
                    <button
                      className="carousel-arrow"
                      onClick={() => cycleFeatured(-1)}
                      aria-label="Previous slide"
                    >
                      ‹
                    </button>
                    <div className="carousel-dots">
                      {featuredGames.map((game) => (
                        <button
                          key={game.key}
                          className={`carousel-dot ${game.key === activeFeatured.key ? 'active' : ''}`}
                          onClick={() => setActiveFeaturedGame(game.key)}
                          aria-label={`Go to ${game.title}`}
                        />
                      ))}
                    </div>
                    <button
                      className="carousel-arrow"
                      onClick={() => cycleFeatured(1)}
                      aria-label="Next slide"
                    >
                      ›
                    </button>
                  </div>

                  <div className="featured-slide-labels">
                    {featuredGames.map((game) => (
                      <button
                        key={`label-${game.key}`}
                        className={`slide-label ${game.key === activeFeatured.key ? 'active' : ''}`}
                        onClick={() => setActiveFeaturedGame(game.key)}
                      >
                        {game.key}
                      </button>
                    ))}
                  </div>
                </article>
              </div>
            </div>
          </section>

          {!isGuest && hasActiveResumeCards && (
            <section className="matchmaking-section">
              <div className="section-header">
                <h2 className="section-title">Ongoing Match</h2>
                <p className="section-subtitle">Jump straight back into your live room</p>
              </div>

              {activeChallenge && (
                <div className="active-challenge-card">
                  <div className="challenge-icon">⚔️</div>
                  <div className="challenge-info">
                    <strong>Active Challenge</strong>
                    <span>Continue your ongoing match</span>
                  </div>
                  <button className="btn-resume" onClick={() => navigate(`/challenges/${activeChallenge.id}`)}>
                    Resume
                  </button>
                </div>
              )}

              {activeMultiplayerChallenge && (
                <div className="active-challenge-card">
                  <div className="challenge-icon">🎮</div>
                  <div className="challenge-info">
                    <strong>Multiplayer Game</strong>
                    <span>In progress - {activeMultiplayerChallenge.game_status}</span>
                  </div>
                  <button className="btn-resume" onClick={() => navigate(`/multiplayer/game/${activeMultiplayerChallenge.id}`)}>
                    Resume
                  </button>
                </div>
              )}

              {activeWhotRoom && (
                <div className="active-challenge-card active-challenge-card--whot">
                  <div className="challenge-icon">🃏</div>
                  <div className="challenge-info">
                    <strong>WHOT Room Active</strong>
                    <span>
                      {String(activeWhotRoom.gameStatus || '').toLowerCase() === 'in_progress'
                        ? 'Your WHOT game is ongoing'
                        : 'Your WHOT room is waiting for players'}
                    </span>
                  </div>
                  <button className="btn-resume" onClick={() => navigate(`/whot-online/room/${activeWhotRoom.roomId}`)}>
                    Open Now
                  </button>
                </div>
              )}

              {activeGameCards.map((card) => (
                <div key={card.key} className="active-challenge-card">
                  <div className="challenge-icon">{card.icon}</div>
                  <div className="challenge-info">
                    <strong>{card.label}</strong>
                    <span>{card.subtitle}</span>
                  </div>
                  <button className="btn-resume" onClick={() => navigate(card.to)}>
                    Open Now
                  </button>
                </div>
              ))}
            </section>
          )}

          {/* Quick Stats Bar */}
          {!isGuest && (
            <div className="stats-bar">
              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-info">
                  <span className="stat-label">Balance</span>
                  <span className="stat-value">{Number(user?.balance || 0)} wikipenny</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🏆</div>
                <div className="stat-info">
                  <span className="stat-label">Win Rate</span>
                  <span className="stat-value">{winRate}%</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🎯</div>
                <div className="stat-info">
                  <span className="stat-label">W/L</span>
                  <span className="stat-value">{wins}/{losses}</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">♟️</div>
                <div className="stat-info">
                  <span className="stat-label">Chess ELO</span>
                  <span className="stat-value">{user?.chess_rating || 1200}</span>
                </div>
              </div>
            </div>
          )}

          {/* Game Modes Grid */}
          <section className="game-modes-section" ref={quickLaunchSectionRef}>
            <div className="section-header">
              <h2 className="section-title">Game Modes</h2>
              <p className="section-subtitle">Choose your battle</p>
            </div>
            
            <div className="game-modes-grid">
              {gameModes.map((mode) => (
                <button
                  key={mode.key}
                  className={`game-mode-card ${mode.isMain ? 'featured-mode' : ''}`}
                  onClick={() => openMainGame(mode.to)}
                >
                  {mode.badge && (
                    <div className={`mode-badge ${mode.badge === 'HOT' ? 'hot-badge' : 'pending-badge'}`}>
                      {mode.badge}
                    </div>
                  )}
                  <div className={`mode-icon-wrapper ${mode.gradient}`}>
                    <span className="mode-icon">{mode.icon}</span>
                  </div>
                  <h3 className="mode-title">{mode.title}</h3>
                  <p className="mode-description">{mode.description}</p>
                  <div className="mode-play-btn">Play Now →</div>
                </button>
              ))}
            </div>
          </section>

          {isGuest && (
            <section className="guest-section">
              <div className="guest-card">
                <h3>Guest Mode</h3>
                <p>Try guest games or login for full access</p>
                <div className="guest-buttons">
                  <button className="btn-guest" onClick={openAnonymousWhot}>Play WHOT Guest</button>
                  <button className="btn-guest" onClick={openAnonymousGame}>Play Flappy Guest</button>
                  <button className="btn-login" onClick={() => navigate('/login', { state: { backgroundLocation: location } })}>
                    Login / Register
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Daily Challenges Section */}
          <section className="daily-section">
            <div className="section-header">
              <h2 className="section-title">Daily Challenges</h2>
              <p className="section-subtitle">Refer friends &amp; play — grow your earnings every day</p>
            </div>
            
            <div className="daily-grid">
              {dailyChallenges.map((challenge, idx) => (
                <div key={idx} className={`daily-card${challenge.isReferral ? ' daily-card--referral' : ''}`}>
                  <div className="daily-icon">{challenge.icon}</div>
                  <div className="daily-info">
                    <h3 className="daily-game">{challenge.game}</h3>
                    <p className="daily-challenge">{challenge.challenge}</p>
                    {challenge.isReferral && referralCode && (
                      <div className="daily-referral-actions">
                        <span className="daily-ref-code">{referralCode}</span>
                        <button
                          className="daily-ref-copy"
                          onClick={() => copyReferralValue(referralLink || referralCode, 'Referral link')}
                        >
                          Copy Link
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="daily-reward">{challenge.reward}</div>
                </div>
              ))}
              <button
                type="button"
                className="daily-card"
                onClick={() => openMainGame('/rewards')}
                style={{ textAlign: 'left', cursor: 'pointer' }}
              >
                <div className="daily-icon">🎁</div>
                <div className="daily-info">
                  <h3 className="daily-game">Daily Rewards</h3>
                  <p className="daily-challenge">Open your daily reward center from the menu</p>
                </div>
                <div className="daily-reward">Claim</div>
              </button>
            </div>
          </section>

          {/* Quick Links */}
          <section className="quick-links-section">
            <div className="quick-links-grid">
              <button className="quick-link-card" onClick={() => navigate('/leaderboard')}>
                <span className="quick-icon">🏅</span>
                <span className="quick-label">Leaderboard</span>
                <span className="quick-desc">Top players</span>
              </button>
              <button className="quick-link-card" onClick={() => openMainGame('/history')}>
                <span className="quick-icon">📜</span>
                <span className="quick-label">Game History</span>
                <span className="quick-desc">Your matches</span>
              </button>
              <button className="quick-link-card" onClick={() => openMainGame('/ledger')}>
                <span className="quick-icon">💳</span>
                <span className="quick-label">Transactions</span>
                <span className="quick-desc">wikipenny history</span>
              </button>
              <button className="quick-link-card" onClick={() => navigate('/guide')}>
                <span className="quick-icon">📘</span>
                <span className="quick-label">Game Guide</span>
                <span className="quick-desc">How to play</span>
              </button>
              <button className="quick-link-card" onClick={scrollToQuickLaunch}>
                <span className="quick-icon">🎮</span>
                <span className="quick-label">All Games</span>
                <span className="quick-desc">Play more</span>
              </button>
              {/* <button className="quick-link-card" onClick={scrollToSupport}>
                <span className="quick-icon">Help</span>
                <span className="quick-label">Support</span>
                <span className="quick-desc">Payment help</span>
              </button> */}
            </div>
          </section>

          {/* Matchmaking Section */}
          {!isGuest && (
            <section className="matchmaking-section">
              <div className="section-header">
                <h2 className="section-title">Quick Match</h2>
                <p className="section-subtitle">Find an opponent instantly</p>
              </div>

              {error && <div className="error-message">{error}</div>}
              {queueStatus && <div className="queue-status">{queueStatus}</div>}

              {!inQueue && !activeChallenge && !activeMultiplayerChallenge && !activeWhotRoom && activeGameCards.length === 0 ? (
                <div className="matchmaking-controls">
                  <div className="wager-control">
                    <label className="wager-label">Wager Amount</label>
                    <div className="wager-value-display">{wager} wikipenny</div>
                    <input
                      type="range"
                      className="wager-slider"
                      min={minimumWager}
                      max={Math.max(minimumWager, Number(user?.balance || 0))}
                      value={Number.isFinite(Number(wager)) ? Number(wager) : minimumWager}
                      onChange={(e) => {
                        const next = Math.trunc(Number(e.target.value) || minimumWager);
                        const cap = Math.max(minimumWager, Number(user?.balance || 0));
                        setWager(Math.min(cap, Math.max(minimumWager, next)));
                      }}
                    />
                    <div className="balance-hint">Balance: {Number(user?.balance || 0)} wikipenny</div>
                  </div>
                  <button className="btn-find-match" onClick={handleJoinQueue}>
                    Find Match
                  </button>
                </div>
              ) : !activeChallenge && (
                <button className="btn-leave-queue" onClick={handleLeaveQueue}>
                  Leave Queue
                </button>
              )}
            </section>
          )}

          {false && isGuest && (
            <section className="guest-section">
              <div className="guest-card">
                <h3>Guest Mode</h3>
                <p>Try anonymous games or login for full access</p>
                <div className="guest-buttons">
                  <button className="btn-guest" onClick={openAnonymousGame}>Play Anonymous</button>
                  <button className="btn-login" onClick={() => navigate('/login', { state: { backgroundLocation: location } })}>
                    Login / Register
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Referral Section */}
          {!isGuest && (
            <section className="referral-section">
              <div className="section-header">
                <h2 className="section-title">Invite Friends</h2>
                <p className="section-subtitle">Earn {referralBonusPercent}% on their deposits</p>
              </div>
              
              <div className="referral-content">
                <div className="referral-code-card">
                  <div className="code-label">Your Referral Code</div>
                  <div className="code-value">{referralCode}</div>
                  <button className="btn-copy" onClick={() => copyReferralValue(referralCode, 'Referral code')}>
                    Copy Code
                  </button>
                </div>
                
                <div className="referral-link-card">
                  <div className="link-label">Referral Link</div>
                  <div className="link-value">{referralLink}</div>
                  <div className="link-actions">
                    <button className="btn-copy" onClick={() => copyReferralValue(referralLink, 'Referral link')}>
                      Copy Link
                    </button>
                    <button className="btn-share" onClick={shareReferralOnWhatsApp}>
                      Share via WhatsApp
                    </button>
                  </div>
                </div>
              </div>
              
              {referralStatus && <div className="referral-feedback">{referralStatus}</div>}
            </section>
          )}

          <section className="support-section" ref={supportSectionRef}>
            <div className="section-header">
              <h2 className="section-title">Support Contacts</h2>
              <p className="section-subtitle">Players can reach us here for deposits, withdrawals, account issues, and general help.</p>
            </div>

            {/* <div className="support-callout">
              Cryptomus moderation note: replace the placeholder contacts below with your real support details before submitting a new merchant.
            </div> */}

            <div className="support-grid">
              {supportContacts.map((contact) => (
                <a
                  key={contact.label}
                  className="support-card"
                  href={contact.href}
                  target={contact.href.startsWith('http') ? '_blank' : undefined}
                  rel={contact.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                >
                  <span className="support-label">{contact.label}</span>
                  <strong className="support-value">{contact.value}</strong>
                  <span className="support-hint">{contact.hint}</span>
                </a>
              ))}
            </div>
          </section>
        </main>

        {/* Profile Menu Modal */}
        {showProfileMenu && (
          <>
            <div className="menu-backdrop" onClick={() => setShowProfileMenu(false)} />
            <div className="profile-menu-modal">
              <div className="profile-header">
                <div className="profile-avatar">{user?.username?.charAt(0) || 'U'}</div>
                <h3 className="profile-name">{user?.username || 'Player'}</h3>
              </div>
              <div className="profile-stats">
                <div className="profile-stat">
                  <span>wikipenny</span>
                  <strong>{Number(user?.balance || 0)}</strong>
                </div>
                <div className="profile-stat">
                  <span>Wins</span>
                  <strong>{wins}</strong>
                </div>
                <div className="profile-stat">
                  <span>Losses</span>
                  <strong>{losses}</strong>
                </div>
                <div className="profile-stat">
                  <span>Win Rate</span>
                  <strong>{winRate}%</strong>
                </div>
                <div className="profile-stat">
                  <span>Chess Rating</span>
                  <strong>{user?.chess_rating ?? 1200}</strong>
                </div>
              </div>
              <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 4 }}>Game Sound</div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {globalSoundEnabled ? 'On for all games' : 'Off for all games'}
                  </div>
                </div>
                <button
                  type="button"
                  className="cm-icon-btn"
                  onClick={() => setGlobalSoundEnabled(!globalSoundEnabled)}
                  title={globalSoundEnabled ? 'Mute all games' : 'Enable all game sounds'}
                  style={{ minWidth: 52, height: 42, borderRadius: 999, padding: '0 14px' }}
                >
                  <span>{globalSoundEnabled ? '🔊' : '🔇'}</span>
                </button>
              </div>
            </div>
          </>
        )}

        {/* Friends and Chat Components */}
        {!isGuest && (
          <Friends
            userId={user?.id}
            openSignal={friendsOpenSignal}
            hideTrigger
            onMessageClick={(friend) => {
              setSelectedFriendForChat(friend.friend_id);
              setShowChat(true);
            }}
          />
        )}

        {!isGuest && showChat && (
          <Chat 
            userId={user?.id} 
            userName={user?.username}
            selectedFriendId={selectedFriendForChat}
            onClose={() => {
              setShowChat(false);
              setSelectedFriendForChat(null);
            }}
          />
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;




